import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit"
import { LatLngBounds, LatLngTuple } from "leaflet"

import { Client, DataStatus, DeliveryZone, RootState, SetTemporaryDistanceLimitProps, Team } from "@/types"
import { fetchClientDeliveryZone, fetchClients, fetchSetTemporaryDistanceLimit } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import { setCityBounds, setCurrentTeam } from "./teams"
import { clearDataOnLogout } from "./user"

const clientsAdapter = createEntityAdapter<Client>()

type ClientsState = EntityState<Client> & {
  status: DataStatus | "updating" | null
  deliveryZone: {
    clientId: Client["id"]
    data: DeliveryZone
  } | null
}

const initialState: ClientsState = clientsAdapter.getInitialState({
  status: "no_team",
  deliveryZone: null,
})

export const getClients = createAsyncThunk("clients/getClients", async (teamsIds: Array<Team["id"]>, { dispatch }) => {
  const { response, json } = await fetchClients(teamsIds)

  if (response.ok && json.data?.length) {
    const clients = json.data
    const bounds = new LatLngBounds(clients.map((client) => [client.lat, client.lng] as LatLngTuple))

    dispatch(setCityBounds(bounds))
  }

  return {
    ok: response.ok,
    status: response.status,
    data: json.data,
  }
})

export const updateClients = createAction<Client[]>("clients/updateClients")

export const updateTemporaryDistanceLimit = createAsyncThunk(
  "clients/updateTemporaryDistanceLimit",
  async ({ clientId, body }: SetTemporaryDistanceLimitProps, { dispatch }) => {
    const {
      response,
      json: { data: updatedClient, errors },
    } = await fetchSetTemporaryDistanceLimit({
      clientId,
      body,
    })

    if (!response.ok || !updatedClient || errors) {
      handleStandardError(response, errors)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateClient(updatedClient))

    return {
      ok: response.ok,
      status: response.status,
      data: updatedClient,
    }
  }
)

export const getClientDeliveryZone = createAsyncThunk("clients/getDeliveryZone", async (clientId: Client["id"]) => {
  const { response, json } = await fetchClientDeliveryZone(clientId)

  if (!response.ok || !json?.data || json?.errors) {
    return {
      ok: response.ok,
      status: response.status,
    }
  }

  return {
    ok: response.ok,
    status: response.status,
    clientId,
    data: json.data,
  }
})

export const clearGlobalClients = createAsyncThunk("clients/clearGlobalClients", (_, { dispatch, getState }) => {
  const {
    teams: { current: currentTeam },
    clients: { entities: clientsEntities },
  } = getState() as RootState

  dispatch(
    removeClients(
      Object.values(clientsEntities)
        .filter((clientEntity) => clientEntity?.team_id !== currentTeam?.id)
        .map((entity) => entity?.id as number)
    )
  )
})

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    updateClient(state, action: PayloadAction<Client>) {
      const updatedClient = action.payload

      clientsAdapter.upsertOne(state, updatedClient)
    },
    removeClients(state, action: PayloadAction<Array<Client["id"]>>) {
      clientsAdapter.removeMany(state, action.payload)
    },
    clearDeliveryZone(state) {
      state.deliveryZone = null
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getClients.pending, (state) => {
      if (state.ids.length) {
        state.status = "updating"
      } else {
        state.status = "loading"
      }
    })

    builder.addCase(getClients.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) clientsAdapter.upsertMany(state, payload.data)
        state.status = "fetched"
      } else {
        state.status = "error"
      }
    })

    builder.addCase(getClientDeliveryZone.fulfilled, (state, { payload }) => {
      if (payload.ok && payload.clientId && payload.data) {
        state.deliveryZone = {
          clientId: payload.clientId,
          data: payload.data,
        }
      }
    })

    builder.addCase(setCurrentTeam, (state) => {
      clientsAdapter.removeAll(state)
    })

    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const { selectById, selectIds, selectAll } = clientsAdapter.getSelectors((state: RootState) => state.clients)

export const selectClientsIds = selectIds as (state: RootState) => Array<Client["id"]>

export const selectClients = selectAll

export const selectClientById = (id: Client["id"]) => (state: RootState) => selectById(state, id)

export const selectClientIdKey = <T extends keyof Client>(id: Client["id"], key: T) =>
  createSelector(selectClientById(id), (client) => client?.[key])

export const selectClientsStatus = (state: RootState) => state.clients.status

export const selectFilteredClients = (...filters: Array<(client: Client) => boolean>) =>
  createSelector(selectClients, (clients) => clients.filter((client) => filters.every((filter) => filter(client))))

export const selectFilteredClientsIds = (...filters: Array<(client: Client) => boolean>) =>
  createSelector(selectFilteredClients(...filters), (clients) => clients.map((client) => client.id))

export const selectSortedFilteredClientsIds = (
  sorter: ((a: Client, b: Client) => number) | null,
  ...filters: Array<(client: Client) => boolean>
) =>
  createSelector(selectFilteredClients(...filters), (clients) => {
    return sorter ? clients.sort(sorter).map((client) => client.id) : clients.map((client) => client.id)
  })

export const selectClientDeliveryZone = (state: RootState) => state.clients.deliveryZone

export const { updateClient, removeClients, clearDeliveryZone } = clientsSlice.actions

export const clientsReducer = clientsSlice.reducer
